// @ts-ignore - vendored self-contained Three.js module.
import * as THREE from '../../vendor/three.module.min.js';
import type { LevelPalette } from '../game/types.js';

export interface TankPalette {
  primary: number;
  secondary: number;
  trim: number;
  dark: number;
  signal: number;
  glass: number;
}

export class MaterialLibrary {
  /** 坦克材质缓存：key 为 palette 六色组合，命中后整套复用，避免高频生成坦克时重复建材质。 */
  private readonly tankCache = new Map<string, Record<string, any>>();
  /** 缓存命中/未命中计数，供调试面板自证命中率。 */
  readonly tankCacheStats = { hits: 0, misses: 0 };
  readonly gradientMap: any;
  readonly outline: any;
  readonly darkRubber: any;
  readonly darkMetal: any;
  readonly white: any;
  readonly hazard: any;
  readonly glass: any;
  readonly shadow: any;
  readonly projectilePlayer: any;
  readonly projectileEnemy: any;
  readonly projectilePlayerTrail: any;
  readonly projectileEnemyTrail: any;

  constructor() {
    const gradientData = new Uint8Array([
      35, 35, 35, 255,
      105, 105, 105, 255,
      190, 190, 190, 255,
      255, 255, 255, 255,
    ]);
    this.gradientMap = new THREE.DataTexture(gradientData, 4, 1, THREE.RGBAFormat);
    this.gradientMap.minFilter = THREE.NearestFilter;
    this.gradientMap.magFilter = THREE.NearestFilter;
    this.gradientMap.needsUpdate = true;
    this.markPersistent(this.gradientMap);

    this.outline = this.markPersistent(new THREE.MeshBasicMaterial({ color: 0x17283d, side: THREE.BackSide, toneMapped: false }));
    this.darkRubber = this.markPersistent(this.toon(0x253044, { roughness: 1 }));
    this.darkMetal = this.markPersistent(this.toon(0x34465a, { roughness: 0.55, metalness: 0.25 }));
    this.white = this.markPersistent(this.toon(0xfff4da, { roughness: 0.78 }));
    this.hazard = this.markPersistent(this.toon(0xff6b57, { roughness: 0.72 }));
    // transmission 禁用：任何 >0 的 transmission 都会触发 three.js 整场景二次渲染
    // （renderTransmissionPass），透明质感统一用 transparent+opacity+clearcoat 表达。
    this.glass = this.markPersistent(new THREE.MeshPhysicalMaterial({
      color: 0x9cecff,
      roughness: 0.12,
      metalness: 0,
      transparent: true,
      opacity: 0.78,
      clearcoat: 1,
      clearcoatRoughness: 0.18,
    }));
    this.shadow = this.markPersistent(new THREE.MeshBasicMaterial({ color: 0x142235, transparent: true, opacity: 0.22, depthWrite: false }));
    this.projectilePlayer = this.markPersistent(new THREE.MeshStandardMaterial({ color: 0xfff179, emissive: 0xffa52c, emissiveIntensity: 1.6, roughness: 0.34 }));
    this.projectileEnemy = this.markPersistent(new THREE.MeshStandardMaterial({ color: 0xff799f, emissive: 0xff235b, emissiveIntensity: 1.45, roughness: 0.4 }));
    this.projectilePlayerTrail = this.markPersistent(new THREE.MeshBasicMaterial({ color: 0xffc44f, transparent: true, opacity: 0.46, depthWrite: false, toneMapped: false }));
    this.projectileEnemyTrail = this.markPersistent(new THREE.MeshBasicMaterial({ color: 0xff3f78, transparent: true, opacity: 0.46, depthWrite: false, toneMapped: false }));

    // 暴露缓存命中率到 window，便于调试自证（仅调试用途，不影响逻辑）。
    if (typeof window !== 'undefined') {
      (window as any).__toonTankCache ??= {};
      (window as any).__toonTankCache.tankMaterials = this.tankCacheStats;
    }
  }

  toon(color: number, extras: Record<string, unknown> = {}): any {
    const clean = { ...extras } as Record<string, unknown>;
    delete clean.roughness;
    delete clean.metalness;
    delete clean.clearcoat;
    delete clean.clearcoatRoughness;
    return new THREE.MeshToonMaterial({ color, gradientMap: this.gradientMap, ...clean });
  }

  standard(color: number, extras: Record<string, unknown> = {}): any {
    return new THREE.MeshStandardMaterial({ color, roughness: 0.7, metalness: 0.05, ...extras });
  }

  emissive(color: number, intensity = 1.2): any {
    return new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: intensity,
      roughness: 0.38,
      metalness: 0.05,
    });
  }

  tank(palette: TankPalette): Record<string, any> {
    // 按 palette 六色组合做 key；同 palette 重复生成坦克直接复用整套材质。
    const key = [palette.primary, palette.secondary, palette.trim, palette.dark, palette.signal, palette.glass]
      .map((color) => color.toString(16).padStart(6, '0'))
      .join('-');
    const cached = this.tankCache.get(key);
    if (cached) {
      this.tankCacheStats.hits += 1;
      return cached;
    }
    this.tankCacheStats.misses += 1;
    const materials: Record<string, any> = {
      primary: this.toon(palette.primary, { roughness: 0.64 }),
      secondary: this.toon(palette.secondary, { roughness: 0.73 }),
      trim: this.toon(palette.trim, { roughness: 0.42, metalness: 0.14 }),
      dark: this.toon(palette.dark, { roughness: 0.9 }),
      signal: this.emissive(palette.signal, 1.15),
      glass: new THREE.MeshPhysicalMaterial({
        color: palette.glass,
        transparent: true,
        opacity: 0.82,
        roughness: 0.1,
        clearcoat: 1,
        clearcoatRoughness: 0.12,
      }),
    };
    // 缓存材质全部标记 persistent，clearWorld/实体销毁路径会跳过，确保共享项不被 dispose。
    for (const material of Object.values(materials)) this.markPersistent(material);
    this.tankCache.set(key, materials);
    return materials;
  }

  createGroundTexture(palette: LevelPalette, scenery: string): any {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const context = canvas.getContext('2d')!;
    context.fillStyle = palette.groundA;
    context.fillRect(0, 0, 512, 512);

    const cell = 64;
    for (let z = 0; z < 8; z += 1) {
      for (let x = 0; x < 8; x += 1) {
        if ((x + z) % 2 === 0) {
          context.fillStyle = palette.groundB;
          context.fillRect(x * cell, z * cell, cell, cell);
        }
      }
    }

    context.strokeStyle = palette.groundLine;
    context.globalAlpha = 0.32;
    context.lineWidth = 3;
    for (let i = 0; i <= 8; i += 1) {
      context.beginPath();
      context.moveTo(i * cell, 0);
      context.lineTo(i * cell, 512);
      context.stroke();
      context.beginPath();
      context.moveTo(0, i * cell);
      context.lineTo(512, i * cell);
      context.stroke();
    }

    const random = this.seededRandom(scenery === 'meadow' ? 101 : scenery === 'desert' ? 202 : 303);
    context.globalAlpha = 0.18;
    for (let i = 0; i < 220; i += 1) {
      const x = random() * 512;
      const y = random() * 512;
      const radius = 1 + random() * (scenery === 'moon' ? 5 : 2.5);
      context.fillStyle = random() > 0.45 ? '#ffffff' : '#20334a';
      context.beginPath();
      context.arc(x, y, radius, 0, Math.PI * 2);
      context.fill();
    }

    if (scenery === 'desert') {
      context.strokeStyle = '#7f4e3a';
      context.globalAlpha = 0.15;
      for (let i = 0; i < 14; i += 1) {
        context.beginPath();
        context.arc(random() * 512, random() * 512, 18 + random() * 38, 0.2, 2.7);
        context.stroke();
      }
    }

    context.globalAlpha = 1;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(3.5, 2.7);
    texture.anisotropy = 4;
    return texture;
  }

  createHazardTexture(base = '#ffd84d', stripe = '#24344a'): any {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 64;
    const context = canvas.getContext('2d')!;
    context.fillStyle = base;
    context.fillRect(0, 0, 128, 64);
    context.fillStyle = stripe;
    for (let x = -64; x < 192; x += 32) {
      context.beginPath();
      context.moveTo(x, 64);
      context.lineTo(x + 22, 64);
      context.lineTo(x + 54, 0);
      context.lineTo(x + 32, 0);
      context.closePath();
      context.fill();
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(2, 1);
    return texture;
  }


  private markPersistent<T extends { userData?: Record<string, unknown> }>(resource: T): T {
    resource.userData ??= {};
    resource.userData.persistent = true;
    return resource;
  }

  private seededRandom(seed: number): () => number {
    let value = seed >>> 0;
    return () => {
      value = (value * 1664525 + 1013904223) >>> 0;
      return value / 4294967296;
    };
  }
}
